import React from 'react';

import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {calculateLinkLineLayers, getLinesHeightForSlice} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import {useRedraw} from 'molbio/maps/linear/useRedraw';
import {getBiointelligentLinkRegionsForSlice} from 'molbio/maps/translateLinksToHighlightRegions';
import Protein from 'molbio/proteins/Protein';

interface ReverseBiointelligentLinkHighlightViewSpaceProps {
  sliceStart: number;
  sliceEnd: number;
  linkTypes: Array<BiointelligentLinkType>;
  polymer: Sequence | Protein;
  complementVisible: boolean;
  mapType: SliceMapType;
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
}

/**
 * A 'spacer' component that does not render anything itself, but makes sure we have the right amount of
 * room for the border lines drawn on the bottom of the sequence map as part of the link highlights.
 *
 * From the perspective of the SliceView framework, the layers on the sequence map stack with no gaps or
 * overlaps. The framework just draws the next layer after adding the `boxHeight` + `vMargin` of the previous.
 * To implement overlapping layers, we've used a method that isn't visible to the framework,
 * where we draw the layer beyond the bounds of its `boxHeight`, and adjust vars so it ends up ending at the
 * same place as the next layer. See the diagram below:
 *
 * What it actually looks like   vs.    what the framework sees:
 *      |-> start highlights             >|highlights box height  -| thinks hlights start here...
 *      |   highlights top lines         <|                        |
 *      |                               ><| highlights vMargin    -| ...and end here
 *      |     | -> residues              >| residues box height
 *      |     |
 *      |     | -> end residues          <|
 *      |   highlights bottom lines      ><| residues vMargin
 *      |-> end highlights
 *
 * As long as the highlights layer and the residues layer end in the same place, this works.
 * However, we now have a situation where the end of the highlights can be variable, due to the stacked lines
 * on the bottom. Since the framework doesn't know about this, it will not know to adjust following layers
 * accordingly, and we can get annotations covering the bottom lines.
 *
 * We address this by including a spacer layer after the residues, which doesn't render anything but just
 * makes sure the framework gives adequate room to accomodate the bottom lines.
 */
export default function ReverseBiointelligentLinkHighlightViewSpace(
  props: ReverseBiointelligentLinkHighlightViewSpaceProps
): JSX.Element {
  const linkRegions = getBiointelligentLinkRegionsForSlice(
    props.sliceStart,
    props.sliceEnd,
    props.polymer,
    props.linkTypes
  );
  const {topLayers, bottomLayers} = calculateLinkLineLayers(linkRegions, props.polymer.length());
  useRedraw({
    redraw: props.redraw,
    shouldRedraw: () => linkRegions.length > 0,
    newBoxHeight: getLinesHeightForSlice(topLayers, bottomLayers).bottomLinesHeight,
  });
  return <></>;
}
